// @ts-nocheck
import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import getColor from "@insite/mobius/utilities/getColor";
import constants from "@store/constants";
import { typographyTitleStyles } from "@store/Themes/common/mixins/typography";
import breakpointValues, { BreakpointValue } from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { Amount, pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import Styleguide from "@toolkit/Framework/Types/Styleguide";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import { css } from "styled-components";

/* Store Custom Code - Start */
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
/* Store Custom Code - End */

const titleStyles = (fontSizes: BreakpointValue<Amount>[]) => css`
    ${typographyTitleStyles(pxToRemArray(fontSizes))}
    ${spacingStyles("1em", "0.5em")}
`;

const typography: Styleguide["typography"] = {
    fontFamilyImportUrl: `${constants.path.fonts}/common.css` /* Store Custom Code */,
    body: {
        size: "inherit",
        weight: "inherit",
        fontFamily: "inherit",
        css: css`
            color: ${getColor("text")};
            font-style: normal;
            text-transform: none;

            ${({ theme }) => `
                font-family: ${theme.design.typography.fontFamily.base};
                font-weight: ${theme.design.typography.weight.base};
                font-size: ${theme.design.typography.size.base};
                line-height: ${theme.design.typography.lineHeight.base};
            `}
        `,
    },
    p: {
        size: "inherit",
        weight: "inherit",
        css: spacingStyles(),
    },
    h1: {
        css: css`
            ${titleStyles([26, null, 40, null, 50])}
            line-height: 1.15; /* Store Custom Code */
        `,
    },
    h2: {
        css: css`
            ${titleStyles([22, null, 30, null, 40])}
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
    h3: {
        css: css`
            ${titleStyles([20, null, 24, null, 30])}
            line-height: ${x => x.theme.design.typography.lineHeight.small /* Store Custom Code */};
        `,
    },
    h4: {
        css: css`
            ${titleStyles([18, null, 20, null, 24])}
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    h5: {
        css: css`
            ${titleStyles([16, null, 18, null, 20])}
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    h6: {
        css: css`
            ${titleStyles([16, null, null, null, 18])}
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    legend: {
        size: "inherit",
        weight: "inherit",
        color: null as any,
        transform: null as any,
        css: css`
            color: ${getColor("text.main")};
            margin-bottom: ${pxToRem(1)};
            text-transform: uppercase;

            ${({ theme }) => css`
                font-size: ${theme.design.typography.size.tiny};
                font-weight: ${theme.design.typography.weight.bold};
                line-height: ${theme.design.typography.lineHeight.base};
            `};

            &&& + ${FormInputWrapper}, &&& + ${FormFieldStyle} {
                margin-top: ${pxToRem(2)};
            }
        `,
    },
    bannerText: {
        css: css`
            ${({ theme }) => css`
                ${breakpointValues(
                    pxToRemArray([18, null, 24, null, 30]), // Store Custom Code
                    "font-size",
                )}
                ${breakpointValues(pxToRemArray([24, null, 28, null, 30]), "margin-bottom") /* Store Custom Code */}
                line-height: 1.15; /* Store Custom Code */

                /* Store Custom Code - Start */
                p {
                    margin-bottom: 0.65em;
                }
                /* Store Custom Code - End */

                &:last-child {
                    margin-bottom: 0;
                }
            `}
        `,
    },
    /* Store Custom Code - Start */
    bannerSecondaryText: {
        css: css`
            ${({ theme }) => css`
                ${breakpointValues(pxToRemArray([18, null, null, null, 24]), "font-size")}
                ${breakpointValues(pxToRemArray([24, null, null, null, 28]), "margin-bottom")}
                line-height: 1.15;

                p {
                    margin-bottom: 0.65em;
                }

                &:last-child {
                    margin-bottom: 0;
                }
            `}
        `,
    },
    /* Store Custom Code - End */
};

/* Store Custom Code - Start */
typography.large = {
    ...typography.p,
    css: css`
        ${typography.p?.css}
        font-size: ${pxToRem(16)};

        ${breakpoint("xl")} {
            font-size: ${pxToRem(17)};
            line-height: 1.6;
        }
    `,
};
/* Store Custom Code - End */

typography.bannerTitle = {
    ...typography.h1,
    forwardAs: "h1",
    css: css`
        ${typography.h1?.css}
        ${breakpointValues(pxToRemArray([20]), "margin-bottom") /* Store Custom Code */}
    `,
};

typography.bannerSecondaryTitle = {
    ...typography.h2,
    forwardAs: "h1",
    css: css`
        ${typography.h2?.css}
        ${breakpointValues(pxToRemArray([18]), "margin-bottom") /* Store Custom Code */}
    `,
};

export default typography;
